import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Button,
  Tooltip,
  CircularProgress,
  Divider,
  TextField,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Checkbox
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslation } from 'react-i18next';
import OtherViewer from './Viewer/OtherViewer';
import PDFViewer from './Viewer/PDFViewer';
import ImageViewer from './Viewer/ImageViewer';
import AudioViewer from './Viewer/AudioViewer';
import ExcelViewer from './Viewer/ExcelViewer';
import HTMLViewer from './Viewer/HTMLViewer';
import TextViewer from './Viewer/TextViewer';
import VideoViewer from './Viewer/VideoViewer';
import EmailViewer from './Viewer/EmailViewer';
import ZipViewer from './Viewer/ZipViewer';
import WordViewer from './Viewer/WordViewer';
import PowerPointViewer from './Viewer/PowerPointViewer';
import CSVViewer from './Viewer/CSVViewer';
import JSONViewer from './Viewer/JSONViewer';
import useStorageUpload from './useStorageUpload';
import StorageBrowserReplaceDialog from './StorageBrowserReplaceDialog';

const StorageBrowser = ({
  open,
  onClose,
  displayPath = '/',
  folders = [],
  files = [],
  loading = false,
  error = '',
  onFolderClick = () => {},
  onNavigateUp = () => {},
  onNavigate = () => {},
  onRefresh = () => {},
  onCreateFolder = () => {},
  onDeleteFile = () => {},
  onDeleteFolder = () => {},
  onRenameFile = () => {},
  onRenameFolder = () => {},
  onDownload = () => {},
  onDownloadAll = () => {},
  currentPath = '',
  canManageStorage = false,
  canWriteStorage = false,
  primaryColor = '#5757b0',
  downloadingFolder = false,
  restrictToUserFolder = false,
  allowedRoots = [],
  activeRoot = '',
  onRootChange = () => {}
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [folderSortMode, setFolderSortMode] = React.useState('name');
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameTarget, setRenameTarget] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState('');
  const [sortMode, setSortMode] = React.useState('name');
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerFile, setViewerFile] = React.useState(null);
  const [pdfViewer, setPdfViewer] = React.useState({ open: false, name: '', path: '' });
  const [imageViewer, setImageViewer] = React.useState({ open: false, name: '', path: '' });
  const [audioViewer, setAudioViewer] = React.useState({ open: false, name: '', path: '' });
  const [excelViewer, setExcelViewer] = React.useState({ open: false, name: '', path: '' });
  const [htmlViewer, setHtmlViewer] = React.useState({ open: false, name: '', path: '' });
  const [textViewer, setTextViewer] = React.useState({ open: false, name: '', path: '' });
  const [videoViewer, setVideoViewer] = React.useState({ open: false, name: '', path: '' });
  const [emailViewer, setEmailViewer] = React.useState({ open: false, name: '', path: '' });
  const [zipViewer, setZipViewer] = React.useState({ open: false, name: '', path: '' });
  const [wordViewer, setWordViewer] = React.useState({ open: false, name: '', path: '' });
  const [pptViewer, setPptViewer] = React.useState({ open: false, name: '', path: '' });
  const [csvViewer, setCsvViewer] = React.useState({ open: false, name: '', path: '' });
  const [jsonViewer, setJsonViewer] = React.useState({ open: false, name: '', path: '' });
  const [isDragActive, setIsDragActive] = React.useState(false);
  const dragDepthRef = React.useRef(0);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = React.useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = React.useState([]);
  const [duplicateUploadNames, setDuplicateUploadNames] = React.useState([]);
  const [selectedItems, setSelectedItems] = React.useState(() => new Set());
  const lastSelectedKeyRef = React.useRef(null);
  const canModifyStorage = Boolean(canWriteStorage);
  const storageUploadTarget = currentPath || '';
  const storageUploadMeta = React.useMemo(() => ({
    path: storageUploadTarget
  }), [storageUploadTarget]);
  const {
    uploading: storageUploading,
    uploadProgress: storageUploadProgress,
    inputRef: storageUploadInputRef,
    handleFiles: handleStorageFiles,
    triggerFileDialog: openStorageUploadDialog
  } = useStorageUpload({
    command: 'uploadStorageFile',
    meta: storageUploadMeta,
    httpEndpoint: '/api/v1/storage/upload',
    successMessage: t('sidebar.storage_upload_success', 'File uploaded to storage'),
    errorMessage: t('sidebar.storage_upload_error', 'Failed to upload file'),
    sendingMessage: t('sidebar.storage_uploading', 'Uploading file...'),
    onSuccess: () => {
      if (onRefresh) {
        onRefresh();
      }
    }
  });

  const storageUploadPercent = Math.min(
    100,
    Math.max(
      0,
      Number.isFinite(storageUploadProgress) ? storageUploadProgress : 0
    )
  );

  const existingFileNames = React.useMemo(() => {
    if (!Array.isArray(files)) return new Set();
    return new Set(files.map((file) => (file?.name || '').toLowerCase()));
  }, [files]);

  const resetPendingUpload = () => {
    setPendingUploadFiles([]);
    setDuplicateUploadNames([]);
    setConfirmReplaceOpen(false);
  };

  const handleFilesWithConfirm = (incomingFiles) => {
    if (!canModifyStorage) return;
    const fileList = Array.isArray(incomingFiles) ? incomingFiles : Array.from(incomingFiles || []);
    if (!fileList.length) return;
    const duplicates = fileList
      .map((file) => file?.name || '')
      .filter((name) => name && existingFileNames.has(name.toLowerCase()));
    if (duplicates.length > 0) {
      setPendingUploadFiles(fileList);
      setDuplicateUploadNames(duplicates);
      setConfirmReplaceOpen(true);
      return;
    }
    handleStorageFiles(fileList);
  };

  const canDropUpload = Boolean(canModifyStorage && storageUploadTarget) && !storageUploading;
  const normalizedDisplayPath = React.useMemo(() => {
    const raw = typeof displayPath === 'string' ? displayPath : '';
    const normalized = raw.replace(/\\/g, '/');
    return normalized || '/';
  }, [displayPath]);
  const displayPathSegments = React.useMemo(
    () => normalizedDisplayPath.split('/').filter(Boolean),
    [normalizedDisplayPath]
  );
  const isDisplayRooted = normalizedDisplayPath.startsWith('/');
  const canNavigateUp = displayPathSegments.length > 0;

  const buildBreadcrumbTarget = React.useCallback((segmentIndex) => {
    if (segmentIndex < 0) {
      return isDisplayRooted ? '/' : '';
    }
    const joined = displayPathSegments.slice(0, segmentIndex + 1).join('/');
    return isDisplayRooted ? `/${joined}` : joined;
  }, [displayPathSegments, isDisplayRooted]);

  const handleBreadcrumbNavigate = React.useCallback((targetPath) => {
    if (!onNavigate) return;
    onNavigate(targetPath || '/');
  }, [onNavigate]);

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canDropUpload) return;
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canDropUpload) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (!canDropUpload) return;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    handleFilesWithConfirm(files);
  };

  const sortedFolders = React.useMemo(() => {
    const list = Array.isArray(folders) ? [...folders] : [];
    const sorted = list.sort((a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' }));
    if (folderSortMode === 'name_desc') {
      return sorted.reverse();
    }
    return sorted;
  }, [folders, folderSortMode]);

  const sortedFiles = React.useMemo(() => {
    const list = Array.isArray(files) ? [...files] : [];
    const getTime = (f) => {
      if (!f?.createdAt) return 0;
      const ts = new Date(f.createdAt).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };
    if (sortMode === 'date_desc') {
      return list.sort((a, b) => getTime(b) - getTime(a));
    }
    if (sortMode === 'date_asc') {
      return list.sort((a, b) => getTime(a) - getTime(b));
    }
    if (sortMode === 'name_desc') {
      return list.sort((a, b) => (b?.name || '').localeCompare(a?.name || '', undefined, { sensitivity: 'base' }));
    }
    // name asc default
    return list.sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
  }, [files, sortMode]);

  React.useEffect(() => {
    setSelectedItems(new Set());
    lastSelectedKeyRef.current = null;
  }, [currentPath, folders, files]);

  const getSelectionKey = React.useCallback((type, name) => `${type}:${name}`, []);

  const selectableItems = React.useMemo(() => [
    ...sortedFolders.map((name) => ({ type: 'folder', name })),
    ...sortedFiles.map((file) => ({ type: 'file', name: file?.name || '' })).filter((item) => item.name)
  ], [sortedFolders, sortedFiles]);

  const selectedItemList = React.useMemo(() => {
    const currentItems = new Map(selectableItems.map((item) => [getSelectionKey(item.type, item.name), item]));
    return Array.from(selectedItems)
      .map((key) => currentItems.get(key))
      .filter(Boolean);
  }, [getSelectionKey, selectableItems, selectedItems]);

  const selectedCount = selectedItemList.length;
  const folderSelectionKeys = React.useMemo(
    () => sortedFolders.map((name) => getSelectionKey('folder', name)),
    [getSelectionKey, sortedFolders]
  );
  const fileSelectionKeys = React.useMemo(
    () => sortedFiles.map((file) => getSelectionKey('file', file?.name || '')).filter(Boolean),
    [getSelectionKey, sortedFiles]
  );
  const allFoldersSelected = folderSelectionKeys.length > 0 && folderSelectionKeys.every((key) => selectedItems.has(key));
  const someFoldersSelected = folderSelectionKeys.some((key) => selectedItems.has(key));
  const allFilesSelected = fileSelectionKeys.length > 0 && fileSelectionKeys.every((key) => selectedItems.has(key));
  const someFilesSelected = fileSelectionKeys.some((key) => selectedItems.has(key));

  const toggleSelection = (type, name, event) => {
    if (!canModifyStorage || !name) return;
    const key = getSelectionKey(type, name);
    const shiftPressed = Boolean(event?.shiftKey || event?.nativeEvent?.shiftKey);
    const nextChecked = event?.target ? Boolean(event.target.checked) : null;
    setSelectedItems((previous) => {
      const next = new Set(previous);
      const selectionKeys = selectableItems.map((item) => getSelectionKey(item.type, item.name));
      const currentIndex = selectionKeys.indexOf(key);
      const anchorIndex = selectionKeys.indexOf(lastSelectedKeyRef.current);

      if (shiftPressed && currentIndex >= 0 && anchorIndex >= 0) {
        const [start, end] = currentIndex < anchorIndex
          ? [currentIndex, anchorIndex]
          : [anchorIndex, currentIndex];
        selectionKeys.slice(start, end + 1).forEach((rangeKey) => {
          if (nextChecked === false) {
            next.delete(rangeKey);
          } else {
            next.add(rangeKey);
          }
        });
      } else if (nextChecked !== null) {
        if (nextChecked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      } else if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    lastSelectedKeyRef.current = key;
  };

  const toggleSelectionGroup = (keys, checked) => {
    if (!canModifyStorage) return;
    lastSelectedKeyRef.current = null;
    setSelectedItems((previous) => {
      const next = new Set(previous);
      keys.forEach((key) => {
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      });
      return next;
    });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  };

  const buildFullPath = (name) => {
    if (!name || !currentPath) return '';
    const separator = currentPath.includes('\\') ? '\\' : '/';
    const trimmedBase = currentPath.endsWith(separator) ? currentPath.slice(0, -1) : currentPath;
    return `${trimmedBase}${separator}${name}`;
  };

  const isExt = (fileName, exts = []) => {
    const lower = (fileName || '').toLowerCase();
    return exts.some((ext) => lower.endsWith(ext));
  };

  const handleOpenViewer = (fileName) => {
    const fullPath = buildFullPath(fileName);
    if (!fullPath) {
      console.warn('[StorageBrowser] Missing path for file', fileName);
      return;
    }

    const isPreviewStreamType = (name) =>
      isExt(name, ['.pdf']) ||
      isExt(name, ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.webp']) ||
      isExt(name, ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma']) ||
      isExt(name, ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv']) ||
      isExt(name, ['.txt', '.log', '.md', '.ini', '.yml', '.yaml', '.sql', '.conf', '.cfg', '.properties', '.csv', '.json']) ||
      isExt(name, ['.docx', '.doc']);

    const openDownloadViewer = () => {
      setViewerFile({ name: fileName, path: fullPath });
      setViewerOpen(true);
    };

    if (restrictToUserFolder && !isPreviewStreamType(fileName)) {
      openDownloadViewer();
      return;
    }

    const openViewer = (setter) => {
      setter({ open: true, name: fileName, path: fullPath });
    };

    if (isExt(fileName, ['.pdf'])) {
      openViewer(setPdfViewer);
    } else if (isExt(fileName, ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.webp'])) {
      openViewer(setImageViewer);
    } else if (isExt(fileName, ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma'])) {
      openViewer(setAudioViewer);
    } else if (isExt(fileName, ['.xlsx', '.xls', '.xlsm'])) {
      openViewer(setExcelViewer);
    } else if (isExt(fileName, ['.html', '.htm'])) {
      openViewer(setHtmlViewer);
    } else if (isExt(fileName, ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv'])) {
      openViewer(setVideoViewer);
    } else if (isExt(fileName, ['.msg', '.eml'])) {
      openViewer(setEmailViewer);
    } else if (isExt(fileName, ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'])) {
      openViewer(setZipViewer);
    } else if (isExt(fileName, ['.docx', '.doc'])) {
      openViewer(setWordViewer);
    } else if (isExt(fileName, ['.pptx', '.ppt'])) {
      openViewer(setPptViewer);
    } else if (isExt(fileName, ['.csv'])) {
      openViewer(setCsvViewer);
    } else if (isExt(fileName, ['.json'])) {
      openViewer(setJsonViewer);
    } else if (isExt(fileName, ['.txt', '.log', '.md', '.ini', '.yml', '.yaml', '.sql', '.conf', '.cfg', '.properties'])) {
      openViewer(setTextViewer);
    } else {
      setViewerFile({ name: fileName, path: fullPath });
      setViewerOpen(true);
    }
  };

  const openDeleteConfirm = (name, type) => {
    if (!canModifyStorage) return;
    setDeleteTarget({ name, type });
  };

  const openBulkDeleteConfirm = () => {
    if (!canModifyStorage || selectedItemList.length === 0) return;
    setDeleteTarget({ type: 'bulk', items: selectedItemList });
  };

  const openRenameDialog = (name, type) => {
    if (!canModifyStorage) return;
    setRenameTarget({ name, type });
    setRenameValue(name || '');
    setRenameOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
  };

  const confirmDelete = () => {
    if (!canModifyStorage) {
      closeDeleteConfirm();
      return;
    }
    if (deleteTarget?.type === 'bulk') {
      (deleteTarget.items || []).forEach((item) => {
        if (item.type === 'folder') {
          onDeleteFolder(item.name);
        } else if (item.type === 'file') {
          onDeleteFile(item.name);
        }
      });
      setSelectedItems(new Set());
      lastSelectedKeyRef.current = null;
    } else if (deleteTarget?.type === 'folder') {
      onDeleteFolder(deleteTarget.name);
    } else if (deleteTarget?.type === 'file') {
      onDeleteFile(deleteTarget.name);
    }
    setDeleteTarget(null);
  };

  const closeRenameDialog = () => {
    setRenameOpen(false);
    setRenameTarget(null);
    setRenameValue('');
  };

  const handleRenameSubmit = () => {
    if (!canModifyStorage) {
      closeRenameDialog();
      return;
    }
    const trimmed = (renameValue || '').trim();
    if (!trimmed || !renameTarget?.name) {
      closeRenameDialog();
      return;
    }
    if (renameTarget.type === 'folder') {
      onRenameFolder(renameTarget.name, trimmed);
    } else if (renameTarget.type === 'file') {
      onRenameFile(renameTarget.name, trimmed);
    }
    closeRenameDialog();
  };

  const handleCreateSubmit = () => {
    if (!canModifyStorage) return;
    const trimmed = (newFolderName || '').trim();
    if (!trimmed) return;
    onCreateFolder(trimmed);
    setCreateOpen(false);
    setNewFolderName('');
  };
  if (!canManageStorage) {
    return null;
  }

  const renderContent = () => {
    if (error) {
      return (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            {t('sidebar.storage_loading', 'Loading storage...')}
          </Typography>
        </Box>
      );
    }

    const folderGridTemplateColumns = canModifyStorage ? '36px 1fr 72px' : '1fr 64px';
    const fileGridTemplateColumns = canModifyStorage
      ? { xs: '36px 1fr auto', md: '36px 2fr 160px 120px' }
      : { xs: '1fr auto', md: '2fr 160px 120px' };

    return (
      <>
      {canModifyStorage && selectedCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            backgroundColor: 'rgba(87,87,176,0.08)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t('sidebar.storage_selected_count', '{{count}} selected', { count: selectedCount })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Button
              size="small"
              onClick={() => {
                setSelectedItems(new Set());
                lastSelectedKeyRef.current = null;
              }}
            >
              {t('sidebar.storage_clear_selection', 'Clear')}
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={openBulkDeleteConfirm}
            >
              {t('sidebar.storage_delete_selected', 'Delete selected')}
            </Button>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
          gap: 1.5
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
              {t('sidebar.storage_folders', 'Folders')} ({sortedFolders.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {canModifyStorage && (
                <Tooltip title={t('sidebar.storage_new_folder', 'New folder')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setCreateOpen(true)}
                      sx={{ border: '1px solid rgba(87, 87, 176, 0.2)' }}
                    >
                      <CreateNewFolderIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: folderGridTemplateColumns,
              alignItems: 'center',
              px: 1,
              py: 0.75,
              mb: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(87,87,176,0.06)'
            }}
          >
            {canModifyStorage && (
              <Checkbox
                size="small"
                checked={allFoldersSelected}
                indeterminate={someFoldersSelected && !allFoldersSelected}
                disabled={folderSelectionKeys.length === 0}
                onChange={(event) => toggleSelectionGroup(folderSelectionKeys, event.target.checked)}
                inputProps={{ 'aria-label': t('sidebar.storage_select_all_folders', 'Select all folders') }}
                sx={{ p: 0.25 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('sidebar.storage_folder_name', 'Name')}
              </Typography>
              <Tooltip title={t('sidebar.storage_sort_name', 'Sort by name')}>
                <IconButton
                  size="small"
                  onClick={() => setFolderSortMode(folderSortMode === 'name_desc' ? 'name' : 'name_desc')}
                >
                  {folderSortMode === 'name_desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right' }}>
              {t('common.actions', 'Actions')}
            </Typography>
          </Box>
          <List dense disablePadding>
            {sortedFolders.length === 0 ? (
              <ListItem disablePadding>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  primary={t('sidebar.storage_no_folders', 'No folders')}
                />
              </ListItem>
            ) : (
              sortedFolders.map((folder) => (
                <ListItemButton
                  key={folder}
                  onClick={() => onFolderClick(folder)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: folderGridTemplateColumns,
                    alignItems: 'center',
                    columnGap: 1,
                    borderRadius: '10px',
                    mb: 0.25,
                    px: 1,
                    py: 0.75,
                    '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
                  }}
                >
                  {canModifyStorage && (
                    <Checkbox
                      size="small"
                      checked={selectedItems.has(getSelectionKey('folder', folder))}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => toggleSelection('folder', folder, event)}
                      inputProps={{ 'aria-label': t('sidebar.storage_select_folder', 'Select folder') }}
                      sx={{ p: 0.25 }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <FolderIcon fontSize="small" sx={{ color: primaryColor }} />
                    <Typography
                      variant="body2"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
                    >
                      {folder}
                    </Typography>
                    <Tooltip title={t('sidebar.storage_open_folder', 'Open folder')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onFolderClick(folder);
                        }}
                      >
                        <SubdirectoryArrowRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {canModifyStorage && (
                      <>
                        <Tooltip title={t('sidebar.storage_rename_folder', 'Rename folder')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRenameDialog(folder, 'folder');
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('sidebar.storage_delete_folder', 'Delete folder')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteConfirm(folder, 'folder');
                            }}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
              {t('sidebar.storage_files', 'Files')} ({sortedFiles.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {canModifyStorage && (
                <Tooltip title={storageUploading ? t('sidebar.storage_uploading', 'Uploading file...') : t('sidebar.storage_upload', 'Upload file')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!storageUploadTarget || storageUploading) return;
                        openStorageUploadDialog();
                      }}
                      disabled={!storageUploadTarget || storageUploading}
                      sx={{ border: '1px solid rgba(87, 87, 176, 0.2)' }}
                    >
                      {storageUploading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <NoteAddIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={downloadingFolder ? t('sidebar.storage_downloading', 'Preparing download...') : t('sidebar.storage_download_all', 'Download all files in this folder')}>
                <span>
                  <IconButton
                    onClick={onDownloadAll}
                    size="small"
                    disabled={loading || downloadingFolder}
                    sx={{ border: '1px solid rgba(87, 87, 176, 0.2)', position: 'relative' }}
                  >
                    {downloadingFolder ? (
                      <CircularProgress size={18} />
                    ) : (
                      <FolderZipIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: fileGridTemplateColumns,
              alignItems: 'center',
              px: 1.5,
              py: 0.75,
              mb: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(87,87,176,0.06)'
            }}
          >
            {canModifyStorage && (
              <Checkbox
                size="small"
                checked={allFilesSelected}
                indeterminate={someFilesSelected && !allFilesSelected}
                disabled={fileSelectionKeys.length === 0}
                onChange={(event) => toggleSelectionGroup(fileSelectionKeys, event.target.checked)}
                inputProps={{ 'aria-label': t('sidebar.storage_select_all_files', 'Select all files') }}
                sx={{ p: 0.25 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('sidebar.storage_file_name', 'Name')}
              </Typography>
              <Tooltip title={t('sidebar.storage_sort_name', 'Sort by name')}>
                <IconButton
                  size="small"
                  onClick={() => setSortMode(sortMode === 'name_desc' ? 'name' : 'name_desc')}
                >
                  {sortMode === 'name_desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('sidebar.storage_created', 'Created')}
              </Typography>
              <Tooltip title={t('sidebar.storage_sort_date', 'Sort by created date')}>
                <IconButton
                  size="small"
                  onClick={() => setSortMode(sortMode === 'date_desc' ? 'date_asc' : 'date_desc')}
                >
                  {sortMode === 'date_desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('common.actions', 'Actions')}
            </Typography>
          </Box>
          <List dense disablePadding>
            {sortedFiles.length === 0 ? (
              <ListItem disablePadding>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  primary={t('sidebar.storage_no_files', 'No files')}
                />
              </ListItem>
            ) : (
              sortedFiles.map((file) => {
                const fullPath = buildFullPath(file.name);
                return (
                  <ListItemButton
                    key={file.name}
                    onClick={() => handleOpenViewer(file.name)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: fileGridTemplateColumns,
                      alignItems: 'center',
                      columnGap: 1,
                      borderRadius: '10px',
                      mb: 0.25,
                      px: 1.5,
                      py: 0.75,
                      '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
                    }}
                  >
                    {canModifyStorage && (
                      <Checkbox
                        size="small"
                        checked={selectedItems.has(getSelectionKey('file', file.name))}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => toggleSelection('file', file.name, event)}
                        inputProps={{ 'aria-label': t('sidebar.storage_select_file', 'Select file') }}
                        sx={{ p: 0.25 }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <DescriptionIcon fontSize="small" sx={{ color: primaryColor }} />
                      <Tooltip title={file.name} placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                      {formatDate(file.createdAt)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title={t('sidebar.storage_download', 'Download')}>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDownload(file.name, fullPath);
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canModifyStorage && (
                        <>
                          <Tooltip title={t('sidebar.storage_rename_file', 'Rename file')}>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openRenameDialog(file.name, 'file');
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('sidebar.storage_delete_file', 'Delete file')}>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDeleteConfirm(file.name, 'file');
                              }}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </ListItemButton>
                );
              })
            )}
          </List>
        </Box>
      </Box>
      </>
    );
  };

  return (
    <>
      <input
        type="file"
        multiple
        ref={storageUploadInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          const selectedFiles = Array.from(event.target?.files || []);
          if (event.target) {
            event.target.value = '';
          }
          if (!selectedFiles.length) return;
          handleFilesWithConfirm(selectedFiles);
        }}
      />
      <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreenDialog}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 16px 46px rgba(20,24,74,0.22)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          pr: 1,
          background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
          color: '#fff',
          pb: 1.25
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t('sidebar.storage_browser_title', 'Storage browser')}
            </Typography>
            <FormControl size="small" sx={{ mt: 0.5, minWidth: 180, maxWidth: 320 }}>
              <Select
                value={activeRoot || allowedRoots[0] || ''}
                onChange={(e) => onRootChange(e.target.value)}
                displayEmpty
                renderValue={(val) => {
                  if (!val && allowedRoots.length === 0) return t('sidebar.storage_root_default', 'Root');
                  const label = (val || '').split(/[\\/]/).filter(Boolean).pop() || val || t('sidebar.storage_root_default', 'Root');
                  return label;
                }}
                sx={{
                  color: '#fff',
                  fontSize: '0.8rem',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  '.MuiSelect-icon': { color: 'rgba(255,255,255,0.8)' },
                  height: 32
                }}
              >
                {allowedRoots.map((root) => {
                  const label = root.split(/[\\/]/).filter(Boolean).pop() || root;
                  return (
                    <MenuItem key={root} value={root}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
              <Button
                size="small"
                onClick={() => handleBreadcrumbNavigate(isDisplayRooted ? '/' : '')}
                sx={{
                  minWidth: 'auto',
                  px: 0.4,
                  py: 0,
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' }
                }}
              >
                /
              </Button>
              {displayPathSegments.map((segment, index) => (
                <React.Fragment key={`${segment}-${index}`}>
                  {index > 0 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                      /
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={() => handleBreadcrumbNavigate(buildBreadcrumbTarget(index))}
                    sx={{
                      minWidth: 'auto',
                      px: 0.4,
                      py: 0,
                      color: '#fff',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' }
                    }}
                  >
                    {segment}
                  </Button>
                </React.Fragment>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title={t('sidebar.storage_up', 'Go up')}>
              <span>
                <IconButton
                  onClick={onNavigateUp}
                  size="small"
                  disabled={!canNavigateUp}
                  sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('sidebar.storage_refresh', 'Refresh')}>
              <span>
                <IconButton
                  onClick={onRefresh}
                  size="small"
                  sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.close', 'Close')}>
              <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      {storageUploading && (
        <Box sx={{ px: 3, pb: 1.25, background: 'rgba(255,255,255,0.08)' }}>
          <LinearProgress
            variant="determinate"
            value={storageUploadPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.25)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.9), rgba(255,255,255,0.6))'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.6 }}>
            {t('sidebar.storage_upload_progress', 'Uploading... {{progress}}%', { progress: Math.round(storageUploadPercent) })}
          </Typography>
        </Box>
      )}
      <Divider />
      <DialogContent
        dividers
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          ...(isDragActive ? { backgroundColor: 'rgba(87, 87, 176, 0.06)' } : null)
        }}
      >
        {isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              inset: 12,
              borderRadius: 2,
              border: '2px dashed rgba(87, 87, 176, 0.5)',
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(87, 87, 176, 0.9)', fontWeight: 600 }}>
              {t('sidebar.storage_drop_upload', 'Drop files to upload')}
            </Typography>
          </Box>
        )}
        {renderContent()}
      </DialogContent>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteConfirm}
        >
        <DialogTitle sx={{ pb: 1 }}>
          {t('sidebar.storage_confirm_title', 'Confirm delete')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {deleteTarget?.type === 'bulk'
              ? t('sidebar.storage_confirm_delete_selected', 'Delete the selected items? Folders and their contents will be removed.')
              : deleteTarget?.type === 'folder'
                ? t('sidebar.storage_confirm_delete_folder', 'Delete this folder and its contents?')
                : t('sidebar.storage_confirm_delete_file', 'Delete this file?')}
          </Typography>
          {deleteTarget?.type === 'bulk' ? (
            <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
              {(deleteTarget.items || []).map((item) => (
                <Typography key={getSelectionKey(item.type, item.name)} variant="body2" sx={{ fontWeight: 700 }}>
                  {item.name}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {deleteTarget?.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setNewFolderName(''); }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('sidebar.storage_new_folder', 'New folder')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.storage_folder_name', 'Folder name')}
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setNewFolderName(''); }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={!newFolderName.trim()}
          >
            {t('common.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={renameOpen}
        onClose={closeRenameDialog}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {renameTarget?.type === 'folder'
            ? t('sidebar.storage_rename_folder', 'Rename folder')
            : t('sidebar.storage_rename_file', 'Rename file')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.storage_new_name', 'New name')}
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleRenameSubmit}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <StorageBrowserReplaceDialog
        open={confirmReplaceOpen}
        onClose={() => resetPendingUpload()}
        onConfirm={() => {
          const filesToUpload = pendingUploadFiles;
          resetPendingUpload();
          if (filesToUpload.length) {
            handleStorageFiles(filesToUpload);
          }
        }}
        duplicateNames={duplicateUploadNames}
        primaryColor={primaryColor}
        t={t}
      />
      <OtherViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        filename={viewerFile?.name || ''}
        filePath={viewerFile?.path || ''}
      />
      <PDFViewer
        open={pdfViewer.open}
        onClose={() => setPdfViewer({ open: false, name: '', path: '' })}
        filePath={pdfViewer.path}
        filename={pdfViewer.name}
      />
      <ImageViewer
        open={imageViewer.open}
        onClose={() => setImageViewer({ open: false, name: '', path: '' })}
        filePath={imageViewer.path}
        filename={imageViewer.name}
      />
      <AudioViewer
        open={audioViewer.open}
        onClose={() => setAudioViewer({ open: false, name: '', path: '' })}
        filePath={audioViewer.path}
        filename={audioViewer.name}
      />
      <ExcelViewer
        open={excelViewer.open}
        onClose={() => setExcelViewer({ open: false, name: '', path: '' })}
        filePath={excelViewer.path}
        filename={excelViewer.name}
      />
      <HTMLViewer
        open={htmlViewer.open}
        onClose={() => setHtmlViewer({ open: false, name: '', path: '' })}
        filePath={htmlViewer.path}
        filename={htmlViewer.name}
      />
      <TextViewer
        open={textViewer.open}
        onClose={() => setTextViewer({ open: false, name: '', path: '' })}
        filePath={textViewer.path}
        filename={textViewer.name}
      />
      <VideoViewer
        open={videoViewer.open}
        onClose={() => setVideoViewer({ open: false, name: '', path: '' })}
        filePath={videoViewer.path}
        filename={videoViewer.name}
      />
      <EmailViewer
        open={emailViewer.open}
        onClose={() => setEmailViewer({ open: false, name: '', path: '' })}
        filePath={emailViewer.path}
        filename={emailViewer.name}
      />
      <ZipViewer
        open={zipViewer.open}
        onClose={() => setZipViewer({ open: false, name: '', path: '' })}
        filePath={zipViewer.path}
        filename={zipViewer.name}
      />
      <WordViewer
        open={wordViewer.open}
        onClose={() => setWordViewer({ open: false, name: '', path: '' })}
        filePath={wordViewer.path}
        filename={wordViewer.name}
      />
      <PowerPointViewer
        open={pptViewer.open}
        onClose={() => setPptViewer({ open: false, name: '', path: '' })}
        filePath={pptViewer.path}
        filename={pptViewer.name}
      />
      <CSVViewer
        open={csvViewer.open}
        onClose={() => setCsvViewer({ open: false, name: '', path: '' })}
        filePath={csvViewer.path}
        filename={csvViewer.name}
      />
      <JSONViewer
        open={jsonViewer.open}
        onClose={() => setJsonViewer({ open: false, name: '', path: '' })}
        filePath={jsonViewer.path}
        filename={jsonViewer.name}
      />
    </Dialog>
    </>
  );
};

export default StorageBrowser;
